/**
 * `AppState` の初期値（spec §A.2 完全準拠）。
 *
 * フィールド順は `types.ts` のブロック順序に従う。値の出典は
 * すべて `docs/happa-designtool-v1.1-spec.md` §A.2 の `DEFAULTS` テーブル。
 *
 * 設計参照:
 * - spec §A.2（DEFAULTS）
 * - spec §6（State）
 *
 * メモ:
 * - layout 派生（resolution / outerW など）は本ファイルでは持たない。
 *   `render/layout.ts` の `computeLayout(state)` が aspectRatio + 比率群から
 *   毎フレーム計算する設計（spec §3.1）。state はパラメータの真実のみを保持。
 * - `seed` は固定値 0.5（spec §A.2）。同 seed + 同設定 → 同結果の再現性は
 *   この初期値からも保証される。Shuffle Seed ボタンは `Math.random()` で
 *   差し替えるが、初回レンダーは決定論的。
 */

import type { AppState } from './types';

/**
 * `AppState` の初期値。
 *
 * spec §A.2 の `DEFAULTS` をそのまま移植。`userFonts: []`、`sceneCache: null`
 * を含む全フィールド網羅。
 */
export const DEFAULT_STATE: AppState = {
  // canvas
  aspectRatio: '1:1',
  // A4 portrait at ~254 dpi 相当（2100 × 2970）。Custom 選択時のみ参照される。
  customAspect: { width: 2100, height: 2970 },

  // layout — Phase J H 表に更新（edgeBand 15、bleed 1.00、textMargin 4）
  gutterPct: 15,
  bleedMult: 1.0,
  textMarginPct: 4,

  // shape — Phase J: jitter 70 / scale 50（既定の見た目を強めの変形へ寄せる）
  vertexCount: 6,
  shapeRandomness: 70,
  shapeScale: 50,

  // animation
  progress: 0,
  isAnimating: false,
  seed: 0.5,
  breath: {
    // Phase J: 呼吸を目立たせる default（amp 70 / period 5 秒）
    pulseAmp: 70,
    pulsePeriod: 5.0,
  },

  // fragmentation — Phase J: count 60（randomness / granularity は F で撤去済、
  // render は `src/render/scene.ts` 内のハードコード値で動く）。
  fragmentCount: 60,

  // explosion motion — Phase J: innerDisp 9 / perimMult 3.60 / speedVariance 50
  innerDispPct: 9,
  perimMult: 3.6,
  speedVariance: 50,

  // colors — プリセットスウォッチ 4 色に整合（2026-07-10 ユーザ指定）:
  // 黒 #000000 / 赤 #ff0037 / 灰 #ebe6e1 / 黒 #000000
  surfaceColor: '#000000',
  innerColor: '#ff0037',
  textColor: '#ebe6e1',
  backgroundColor: '#000000',
  backgroundTransparent: false,
  // 全色アンロックが初期状態。鍵はユーザーが明示的に掛けるもの。
  colorLocks: {
    surfaceColor: false,
    innerColor: false,
    textColor: false,
    backgroundColor: false,
  },
  // モード導入前の Text 導出（低彩度インク）と同一挙動の 'ink' を既定に。
  textMode: 'ink',
  overlays: {
    surface: null,
    inner: null,
    background: null,
    foreground: null,
  },

  // text
  textEnabled: true,
  // Phase J: デフォルト本文を "HAPPA BREWING" に変更。タイトル / サブタイトル
  // と整合。句読点・日本語は含まない（Latin フォント単独で成立）。
  text: 'HAPPA BREWING',
  fontSizePct: 3.5,
  tracking: 10,
  followSilhouette: 50,
  // Phase J（G）: Latin default を Helvetica に変更。OS 標準（macOS / iOS /
  // Windows 多くの環境で即利用可）で、Linux 等で未インストールでも
  // Helvetica Neue → Arial → sans-serif へ自然に fallback する。
  // `DEFAULT_FONTS` の "Helvetica" エントリと family 文字列を完全一致させる
  // ことで、起動時の Font Library dropdown が初期選択状態で開く。
  latinFont: "'Helvetica Neue', Helvetica, Arial, sans-serif",
  japaneseFont: "'Hiragino Kaku Gothic ProN', 'Hiragino Sans', 'Noto Sans JP', sans-serif",

  // fonts
  userFonts: [],

  // export
  exportMode: 'detonate',
  // Phase J: Export Timing Controls。default は Phase I までの挙動を維持
  // （前置き呼吸なし、爆破 2 秒）。
  prePulseDuration: 0,
  detonateDuration: 2,

  // debug toggles
  showSilhouette: false,
  showCells: false,
  showHull: false,
  showOverlay: false,

  // derived cache
  sceneCache: null,
};
